import './feature-selection-interaction.css';
import { InteractionGroup } from 'cadenza/map/interactions/interaction-group';
import { SelectContextMenu } from 'cadenza/map/interactions/select-context-menu';
import { SelectBase } from 'cadenza/map/interactions/base-feature-selection';
import { MapProperty } from 'cadenza/map/map-property';
import { AsyncPointSelectionInteraction } from 'cadenza/map/interactions/async-point-selection-interaction';
import { AsyncRectangleSelectionInteraction } from 'cadenza/map/interactions/async-rectangle-selection-interaction';
import { PolygonFeatureSelection } from 'cadenza/map/interactions/polygon-feature-selection';
import Feature from 'ol/Feature';
import { logger } from 'cadenza/utils/logging';

export class FeatureSelectionInteraction extends InteractionGroup {

  constructor (map, options = {}) {
    const { allowMultipleSelection = true } = options;
    const pointInteraction = new PointFeatureSelectionInteraction(map, options);
    const rectangleInteraction = new RectangleFeatureSelectionInteraction(map, options);
    const polygonInteraction = new PolygonFeatureSelection(map, options);
    super(pointInteraction, rectangleInteraction, polygonInteraction);
    this._allowMultipleSelection = allowMultipleSelection;
    this._pointInteraction = pointInteraction;
    this._rectangleInteraction = rectangleInteraction;
    this._polygonInteraction = polygonInteraction;
  }

  doHandleEvent (event) {
    if (this._allowMultipleSelection && (event.originalEvent.shiftKey || this._rectangleInteraction.isInProgress)) {
      this._rectangleInteraction.isInProgress = true;
      this.setSingleInteractionActive(this._rectangleInteraction, true);
      return this._rectangleInteraction.handleEvent(event);
    } else if (this._allowMultipleSelection && (event.originalEvent.ctrlKey && event.originalEvent.altKey)) {
      this._isPolygonDrawInProgress = true;
      this.setSingleInteractionActive(this._polygonInteraction, true);
      return this._polygonInteraction.handleEvent(event);
    }

    this.setSingleInteractionActive(this._pointInteraction, true);
    return this._pointInteraction._handleEvent(event);
  }

}

class PointFeatureSelectionInteraction extends AsyncPointSelectionInteraction {

  constructor (map, options = {}) {
    super();
    this._selectBase = new SelectBase(map, options);
    if (map.get(MapProperty.CONTEXT_MENU_ENABLED)) {
      this._selectContextMenu = new SelectContextMenu(map, this);
    }
  }

  asyncSelect (mapBrowserEvent, geometry) {
    if (this._selectBase.hasContextMenu()) {
      this._selectContextMenu.prepareToShowContextMenu(mapBrowserEvent);
    }

    this._selectBase
      .selectFeatures(mapBrowserEvent.originalEvent, new Feature(geometry))
      .then(() => {
        if (this._selectBase.hasContextMenu()) {
          this._selectContextMenu.showContextMenuIfSelectedFeaturesExist(mapBrowserEvent);
        }
      })
      .catch(error => logger.error(error));
  }

}

class RectangleFeatureSelectionInteraction extends AsyncRectangleSelectionInteraction {

  #map;

  constructor (map, options = {}) {
    super();
    this._selectBase = new SelectBase(map, options);
    if (map.get(MapProperty.CONTEXT_MENU_ENABLED)) {
      this._selectContextMenu = new SelectContextMenu(map, this);
    }
    this.#map = map;

    this._onPointerMoveHandler = event => this._onPointerMove(event);
  }

  onActivation () {
    super.onActivation();
    this.#map.on('pointermove', this._onPointerMoveHandler);
  }

  onDeactivation () {
    super.onDeactivation();
    this.#map.un('pointermove', this._onPointerMoveHandler);
  }

  _onPointerMove (event) {
    if (this._selectBase.hasContextMenu()) {
      this._selectContextMenu.prepareToShowContextMenu(event);
    }
  }

  asyncSelect (event, geometry) {
    return this._selectBase
      .selectFeatures(event.mapBrowserEvent.originalEvent, new Feature(geometry))
      .then(() => {
        if (this._selectBase.hasContextMenu()) {
          this._selectContextMenu.showContextMenuIfSelectedFeaturesExist(event);
        }
      })
      .catch(error => logger.error('Could not make selection', error));
  }

}
